import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import { SectionLabel, RainbowTop } from '../components/shared';
import SaveButton from '../components/SaveButton';
import EmiratesMap from '../components/EmiratesMap';
import { EMIRATES, IMAGES } from '../mock';
import { fetchEmirates } from '../api';

const Emirates = () => {
  const [list, setList] = useState(EMIRATES);
  useReveal([list.length]);

  useEffect(() => {
    fetchEmirates()
      .then((d) => d && d.length && setList(d))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-[#F6F1E3]">
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
          <img src={IMAGES.palmJumeirah} alt="Aerial view of the UAE coast" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1410] via-[#1A1410]/55 to-[#1A1410]/25" />
          <div className="relative max-w-5xl mx-auto px-5 h-full flex flex-col justify-end pb-10">
            <span className="text-[12px] font-semibold tracking-[0.18em] uppercase text-[#CBA344]">The Emirates</span>
            <h1 className="mt-3 font-display text-[38px] sm:text-[50px] leading-[1.06] font-semibold text-white">
              Compare all seven emirates
            </h1>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 pt-10">
        <p className="reveal text-[19px] leading-relaxed text-[#3a4f63]">
          The UAE is one country made of seven emirates, each with its own character and
          ruler. Dubai gets the headlines, but every one is within a couple of hours’
          drive. Here’s how they compare — so you can pick the base that fits your trip.
        </p>
      </div>

      {/* Overview map */}
      <section className="max-w-5xl mx-auto px-5 pt-10">
        <div className="reveal">
          <SectionLabel>The country at a glance</SectionLabel>
          <h2 className="mt-3 font-display text-[28px] font-semibold text-[#1B1D21]">
            Where the seven emirates sit
          </h2>
          <p className="mt-2 text-[16px] text-[#4B4F58]">
            Tap any marker to jump straight to that emirate’s guide.
          </p>
          <div className="mt-6">
            <EmiratesMap emirates={list} />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="space-y-7">
          {list.map((e, i) => (
            <Link
              key={e.slug}
              to={`/emirates/${e.slug}`}
              className="reveal lift group relative grid md:grid-cols-[320px_1fr] bg-white rounded-2xl overflow-hidden shadow-[0_18px_40px_-28px_rgba(27,29,33,0.45)] border border-[#E6D7A2]"
            >
              <div className="absolute top-4 right-4 z-10">
                <SaveButton item={{ type: 'emirate', ref_id: `emirate-${e.slug}`, title: e.name, subtitle: 'Emirate guide', to: `/emirates/${e.slug}` }} />
              </div>
              <div className="relative h-56 md:h-auto overflow-hidden">
                <img src={e.image} alt={e.name} className="img-zoom w-full h-full object-cover" />
              </div>
              <div className="p-7 md:p-9 flex flex-col justify-center">
                <span className="text-[12px] font-semibold tracking-[0.16em] uppercase text-[#e0564e]">
                  Emirate {e.number}
                </span>
                <h2 className="mt-2 font-display text-[30px] font-semibold text-[#1B1D21]">{e.name}</h2>
                <p className="mt-3 text-[16px] leading-relaxed text-[#4B4F58]">{e.tagline}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {e.bestFor.split(',').map((t) => (
                    <span key={t} className="text-[12px] font-medium rounded-full bg-[#F2ECCF] text-[#8a5a2e] px-3 py-1">
                      {t.trim()}
                    </span>
                  ))}
                </div>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#2F663C] group-hover:text-[#1B1D21] transition-colors">
                  Read the {e.name} guide
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Emirates;
